import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { fromEvent, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { hasPermission, showErrorMessage } from '../../utils/utils';
import { ModalService } from '../../services/modal.service';
import { I18nTranslator } from '../../i18n/translate';
import { DEFAULT_ITEMS_PER_PAGE } from '../../utils/constants';
import { Organisation } from '../../models/organisation.model';
import { Page } from '../../models/page.model';
import { OrganisationService } from '../../services/organisation.service';
import { OrganisationEditComponent } from './organisation-edit.component';

import { KeycloakService } from '../../services/keycloak.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { PaginationModule } from 'ngx-bootstrap/pagination';

@Component({
  standalone: true,
  selector: 'organisations-cmp',
  templateUrl: 'organisations.component.html',
  imports: [RouterLink, RouterLinkActive, RouterOutlet, OrganisationEditComponent, FormsModule, CommonModule, TranslatePipe, PaginationModule],
  providers: [OrganisationService],
})
export class OrganisationComponent implements OnInit, AfterViewInit {
  organisations: Organisation[];

  @ViewChild('term') term: ElementRef;

  public rows: Array<any> = [];

  public page: number = 1;
  public itemsPerPage: number = DEFAULT_ITEMS_PER_PAGE;
  public maxSize: number = 5;
  public numPages: number = 1;
  public length: number = 0;
  public filter: string = '';
  // Négoce owners and managers can edit their organisations (the backend only lists those);
  // creating stays with admin and sector users, deleting with admins only.
  showRef: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;

  constructor(
    private organisationService: OrganisationService,
    private router: Router,
    private modal: ModalService,
    private i18nTranslator: I18nTranslator,
  ) {
    const isAdminOrSector =
      KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN') || KeycloakService.auth.authz.hasRealmRole('ROLE_SECTOR');
    this.showRef = isAdminOrSector;
    this.canCreate = isAdminOrSector;
    this.canEdit =
      isAdminOrSector ||
      KeycloakService.auth.authz.hasRealmRole('ROLE_NEGOCE') ||
      KeycloakService.auth.authz.hasRealmRole('ROLE_MANAGER');
    this.canDelete = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
  }

  getOrganisationsPaginated(pageIndex: number, sortColumnName: string, sortColumnOrder: string, filter: string) {
    this.organisationService
      .getOrganisationsAdvanced(pageIndex, this.itemsPerPage, sortColumnName, sortColumnOrder, filter, true)
      .subscribe((page) => this.handlePage(page));
  }

  handlePage(page: Page<Organisation>) {
    this.rows = page.content;
    this.numPages = page.totalPages;
    this.length = page.totalElements;
  }

  addOrganisation() {
    this.router.navigate(['/organisations', 'new']);
  }

  deleteOrganisation(organisation: Organisation, event: any) {
    event.stopPropagation();
    this.modal
      .confirm(this.i18nTranslator.translate('distributeur.table.delete.confirm', organisation.name))
      .then((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.organisationService.delete(organisation).subscribe(
          (res) => {
            this.onChangeTable();
          },
          (error) => this.handleDeleteError(error),
        );
      });
  }

  handleDeleteError(error: any) {
    // 400: the backend refuses to delete an organisation that still has member users
    const key = error?.status === 400 ? 'distributeur.table.delete.hasUsers' : 'distributeur.table.delete.error';
    showErrorMessage(this.modal, this.i18nTranslator.translate(key));
  }

  editOrganisation(organisation: Organisation, event: any) {
    this.router.navigate(['/organisations', 'edit', organisation.id]);
  }

  ngOnInit() {
    if (hasPermission(['ROLE_ADMIN', 'ROLE_SECTOR', 'ROLE_NEGOCE', 'ROLE_MANAGER'], ['/home'], this.router)) {
      this.onChangeTable();
    }
  }

  ngAfterViewInit() {
    fromEvent(this.term.nativeElement, 'keyup')
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((term: any) => {
        this.filter = <string>term.target.value;
        this.updateFilters();
      });
  }

  updateFilters() {
    // reset la page à la première et update la table
    // FIXME faire mieux : suspendre le traitement des changements ?
    if (this.page === 1) {
      this.onChangeTable();
    } else {
      // le biding (pagechanged) du contrôle de pagination va appeler onChangeTable
      this.page = 1;
    }
  }

  onChangeTable(page: any = { page: this.page, itemsPerPage: this.itemsPerPage }): any {
    console.log('onChangeTable');
    const pageNumber = page.page;
    this.getOrganisationsPaginated(pageNumber, 'id', 'desc', this.filter);
  }
}
